import { useEffect, useState, useCallback } from 'react';
import { shrinkImage } from '../../lib/shrinkImage';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { X, Trash2, Plus, Minus } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { deleteProduct } from '../../lib/deleteProduct';
import ConfirmDialog from '../../components/ConfirmDialog';
import RequestMatchPanel from '../../components/admin/RequestMatchPanel';
import ProductHistory from '../../components/admin/ProductHistory';

const MEDIA_BUCKET = 'shinyvault-media';

function slugify(s) {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

const blank = {
  name: '', slug: '', category_id: '', description: '',
  cost: '', price: '', quantity_available: '0',
  volatility: 'stable', status: 'draft', language: 'English', owner: 'trainer_center',
  weight_oz: '', length_in: '', width_in: '', height_in: '',
  purchased_on: '',
  condition: '', grade: '', rarity: '', set_name: '', number: '',
};

const CONDITION_OPTIONS = [
  'Near Mint', 'Lightly Played', 'Moderately Played', 'Heavily Played',
  'Damaged', 'Sealed', 'Graded',
];

const RARITY_SUGGESTIONS = [
  'Holo Rare', 'Illustration Rare', 'Special Illustration Rare', 'Secret Rare',
  'Promo', 'Common', 'Uncommon', 'Rare',
];

export default function AdminProductEditPage() {
  const { id } = useParams();
  const isNew = id === 'new';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Reached from the bulk-upload Step 4 review batch — Back and Save should
  // return to the review loop (/admin/products/bulk), not the full products list.
  const fromBulk = searchParams.get('from') === 'bulk';

  const [categories, setCategories] = useState([]);
  const [languageOptions, setLanguageOptions] = useState(['English', 'Japanese']);
  const [addingLanguage, setAddingLanguage] = useState(false);
  const [newLanguage, setNewLanguage] = useState('');
  const [form, setForm] = useState(blank);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);

  const [media, setMedia] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [mediaError, setMediaError] = useState('');
  const [dragActive, setDragActive] = useState(false);

  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const handleDelete = async () => {
    setDeleting(true);
    setDeleteError('');
    const { error: err } = await deleteProduct(id);
    setDeleting(false);
    if (err) { setDeleteError(err.message); return; }
    // From the bulk review batch, stay in the loop — the review re-hydrates and
    // simply drops this now-deleted item.
    navigate(fromBulk ? '/admin/products/bulk' : '/admin/products');
  };

  const loadMedia = useCallback(() => {
    if (isNew) return;
    supabase
      .from('product_media')
      .select('*')
      .eq('product_id', id)
      .eq('kind', 'photo')
      .order('sort_order')
      .then(({ data }) => setMedia(data || []));
  }, [id, isNew]);

  useEffect(() => { loadMedia(); }, [loadMedia]);

  const uploadPhotos = async (files) => {
    const imageFiles = files.filter((f) => f.type.startsWith('image/'));
    if (imageFiles.length === 0) return;
    setMediaError('');
    setUploading(true);
    let nextSortOrder = media.length;
    try {
      for (const file of imageFiles) {
        const upload = await shrinkImage(file);
        const ext = upload.name.split('.').pop();
        const path = `${id}/${crypto.randomUUID()}.${ext}`;
        const { error: upErr } = await supabase.storage.from(MEDIA_BUCKET).upload(path, upload);
        if (upErr) throw upErr;
        const { error: insErr } = await supabase.from('product_media').insert({
          product_id: id, kind: 'photo', storage_path: path, sort_order: nextSortOrder,
        });
        if (insErr) throw insErr;
        nextSortOrder += 1;
      }
      loadMedia();
    } catch (e) {
      setMediaError(e.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (uploading) return;
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length) uploadPhotos(files);
  };

  const removePhoto = async (m) => {
    await supabase.storage.from(MEDIA_BUCKET).remove([m.storage_path]);
    await supabase.from('product_media').delete().eq('id', m.id);
    loadMedia();
  };

  useEffect(() => {
    supabase.from('categories').select('id, name, kind').order('sort_order').then(({ data }) => setCategories(data || []));
    // Pull in any custom languages other products already use, so a language
    // typed in once via "add another" becomes a preset for everyone after.
    supabase.from('products').select('language').then(({ data }) => {
      const used = (data || []).map((p) => p.language).filter(Boolean);
      setLanguageOptions((opts) => Array.from(new Set([...opts, ...used])));
    });
  }, []);

  useEffect(() => {
    if (isNew) return;
    supabase.from('products').select('*').eq('id', id).single().then(({ data, error: err }) => {
      if (err) { setError(err.message); setLoading(false); return; }
      setForm({
        name: data.name, slug: data.slug, category_id: data.category_id || '',
        description: data.description || '',
        cost: (data.cost_cents / 100).toString(),
        price: (data.price_cents / 100).toString(),
        quantity_available: data.quantity_available.toString(),
        volatility: data.volatility, status: data.status, language: data.language || 'English',
        owner: data.owner || 'trainer_center',
        weight_oz: data.weight_oz?.toString() || '',
        length_in: data.length_in?.toString() || '',
        width_in: data.width_in?.toString() || '',
        height_in: data.height_in?.toString() || '',
        purchased_on: data.purchased_on || '',
        condition: data.condition ?? '',
        grade: data.grade ?? '',
        rarity: data.rarity ?? '',
        set_name: data.set_name ?? '',
        number: data.number ?? '',
      });
      setSlugTouched(true);
      setLoading(false);
    });
  }, [id, isNew]);

  const handleNameChange = (name) => {
    setForm((f) => ({ ...f, name, slug: slugTouched ? f.slug : slugify(name) }));
  };

  const handleSave = async (e, markPriceChecked = false) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    // The selected category's kind decides which card fields apply, so we don't
    // persist (or ask for) rarity on an ETB, etc. Null out what doesn't apply.
    const kind = categories.find((c) => c.id === form.category_id)?.kind || 'other';
    const cardOK = kind === 'single' || kind === 'other';
    const setOK = cardOK || kind === 'sealed';

    const payload = {
      name: form.name.trim(),
      slug: form.slug.trim(),
      category_id: form.category_id || null,
      description: form.description.trim() || null,
      cost_cents: Math.round(parseFloat(form.cost || '0') * 100),
      price_cents: Math.round(parseFloat(form.price || '0') * 100),
      quantity_available: parseInt(form.quantity_available, 10) || 0,
      volatility: form.volatility,
      status: form.status,
      owner: form.owner,
      language: form.language || 'English',
      weight_oz: form.weight_oz ? parseFloat(form.weight_oz) : null,
      length_in: form.length_in ? parseFloat(form.length_in) : null,
      width_in: form.width_in ? parseFloat(form.width_in) : null,
      height_in: form.height_in ? parseFloat(form.height_in) : null,
      purchased_on: form.purchased_on || null,
      condition: cardOK ? (form.condition || null) : null,
      grade: (cardOK && form.condition === 'Graded') ? (form.grade.trim() || null) : null,
      rarity: cardOK ? (form.rarity.trim() || null) : null,
      number: cardOK ? (form.number.trim() || null) : null,
      set_name: setOK ? (form.set_name.trim() || null) : null,
    };
    if (!(payload.cost_cents > 0) && payload.status === 'active') {
      setError('An active item needs a cost. Profit is the sale price minus the cost, and the 25/75 split is calculated from it.');
      setSaving(false);
      return;
    }

    if (markPriceChecked) payload.price_checked_at = new Date().toISOString();

    if (isNew) {
      const { data, error: err } = await supabase.from('products').insert(payload).select('id').single();
      setSaving(false);
      if (err) { setError(err.message); return; }
      // Go to the edit page for the new product (not the list) so photos
      // can be added right away — media needs a product id to attach to.
      navigate(`/admin/products/${data.id}`, { replace: true });
      return;
    }

    const { error: err } = await supabase.from('products').update(payload).eq('id', id);
    setSaving(false);
    if (err) { setError(err.message); return; }
    navigate(fromBulk ? '/admin/products/bulk' : '/admin/products');
  };

  if (loading) return <div>Loading…</div>;

  const profitCents = Math.round((parseFloat(form.price || '0') - parseFloat(form.cost || '0')) * 100);

  // Which card fields to ask for, based on the chosen category's kind.
  // single/other → condition, rarity, set (+ grade if Graded); sealed → set
  // only; accessory → none. No category yet → show all (nothing lost).
  const kind = categories.find((c) => c.id === form.category_id)?.kind || 'other';
  const showCardFields = kind === 'single' || kind === 'other';
  const showSet = showCardFields || kind === 'sealed';

  return (
    <div style={{ maxWidth: 640 }}>
      <Link to={fromBulk ? '/admin/products/bulk' : '/admin/products'} style={{ fontSize: '0.85rem', color: 'var(--text-muted)', display: 'inline-block', marginBottom: 16 }}>
        {fromBulk ? '← Back to review' : '← Back to products'}
      </Link>
      <h1 style={{ fontSize: '1.5rem', fontWeight: 900, marginBottom: 20 }}>{isNew ? 'New product' : 'Edit product'}</h1>

      <form onSubmit={(e) => handleSave(e, false)}>
        <label style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Name *</label>
        <input className="sv-input" style={{ marginBottom: 14, marginTop: 6 }} value={form.name} onChange={(e) => handleNameChange(e.target.value)} required />

        <label style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Slug *</label>
        <input className="sv-input" style={{ marginBottom: 14, marginTop: 6 }} value={form.slug} onChange={(e) => { setSlugTouched(true); setForm({ ...form, slug: e.target.value }); }} required />

        <label style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Category</label>
        <select className="sv-input" style={{ marginBottom: 14, marginTop: 6 }} value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })}>
          <option value="">— none —</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>

        <label style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Language</label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6, marginBottom: 14, alignItems: 'center' }}>
          {languageOptions.map((lang) => (
            <button
              key={lang}
              type="button"
              onClick={() => { setForm({ ...form, language: lang }); setAddingLanguage(false); }}
              className={form.language === lang ? 'sv-btn' : 'sv-btn-outline'}
              style={{ padding: '7px 14px', fontSize: '0.82rem' }}
            >
              {lang}
            </button>
          ))}
          {addingLanguage ? (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                autoFocus
                className="sv-input"
                placeholder="New language"
                value={newLanguage}
                onChange={(e) => setNewLanguage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const val = newLanguage.trim();
                    if (!val) return;
                    setLanguageOptions((opts) => Array.from(new Set([...opts, val])));
                    setForm({ ...form, language: val });
                    setNewLanguage('');
                    setAddingLanguage(false);
                  } else if (e.key === 'Escape') {
                    setAddingLanguage(false);
                    setNewLanguage('');
                  }
                }}
                style={{ width: 140, padding: '7px 10px', fontSize: '0.82rem' }}
              />
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setAddingLanguage(true)}
              className="sv-btn-outline"
              style={{ padding: '7px 14px', fontSize: '0.82rem' }}
            >
              + Add another
            </button>
          )}
        </div>

        <label style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Description</label>
        <textarea className="sv-input" style={{ marginBottom: 14, marginTop: 6, resize: 'vertical' }} rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />

        <div style={{ display: 'flex', gap: 14, marginBottom: 4 }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Cost paid ($) *</label>
            <input className="sv-input" style={{ marginTop: 6 }} type="number" step="0.01" min="0" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} required />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Sale price ($) *</label>
            <input className="sv-input" style={{ marginTop: 6 }} type="number" step="0.01" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} required />
          </div>
        </div>
        <div style={{ fontSize: '0.85rem', marginBottom: 14, color: profitCents >= 0 ? 'var(--success)' : 'var(--danger)', fontWeight: 700 }}>
          Net profit: ${(profitCents / 100).toFixed(2)}
        </div>

        <label style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Date purchased</label>
        <input className="sv-input" style={{ marginBottom: 4, marginTop: 6 }} type="date" value={form.purchased_on} onChange={(e) => setForm({ ...form, purchased_on: e.target.value })} />
        <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: '0 0 14px' }}>
          Internal only — the date you acquired this item. Never shown to customers.
        </p>

        <div style={{ display: 'flex', gap: 14, marginBottom: 14 }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Quantity available *</label>
            <div style={{ display: 'flex', gap: 6, marginTop: 6, alignItems: 'stretch' }}>
              <button
                type="button"
                aria-label="Decrease quantity"
                onClick={() => setForm((f) => ({ ...f, quantity_available: String(Math.max(0, (parseInt(f.quantity_available, 10) || 0) - 1)) }))}
                className="sv-btn-outline"
                style={{ padding: '0 14px', display: 'inline-flex', alignItems: 'center' }}
              >
                <Minus size={16} />
              </button>
              <input className="sv-input" style={{ textAlign: 'center', flex: 1, minWidth: 0 }} type="number" min="0" value={form.quantity_available} onChange={(e) => setForm({ ...form, quantity_available: e.target.value })} required />
              <button
                type="button"
                aria-label="Increase quantity"
                onClick={() => setForm((f) => ({ ...f, quantity_available: String((parseInt(f.quantity_available, 10) || 0) + 1) }))}
                className="sv-btn-outline"
                style={{ padding: '0 14px', display: 'inline-flex', alignItems: 'center' }}
              >
                <Plus size={16} />
              </button>
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Status</label>
            <select className="sv-input" style={{ marginTop: 6 }} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </div>

        {/* Decides who gets the 75% when this sells, so it is captured at entry
            and snapshotted onto the order line at the moment of sale. */}
        <div style={{ display: 'flex', gap: 14, marginBottom: 14 }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Whose item is this?</label>
            <select className="sv-input" style={{ marginTop: 6 }} value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })}>
              <option value="trainer_center">Trainer Center</option>
              <option value="olin">Olin</option>
            </select>
            <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: '6px 0 0' }}>
              Sets who is paid the 75 percent of profit when it sells.
            </p>
          </div>
        </div>

        {showCardFields && (
          <div style={{ display: 'flex', gap: 14, marginBottom: 14 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Condition</label>
              <select className="sv-input" style={{ marginTop: 6 }} value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })}>
                <option value="">—</option>
                {CONDITION_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            {form.condition === 'Graded' && (
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Grade</label>
                <input className="sv-input" style={{ marginTop: 6 }} placeholder="e.g. PSA 10" value={form.grade} onChange={(e) => setForm({ ...form, grade: e.target.value })} />
              </div>
            )}
          </div>
        )}

        {(showCardFields || showSet) && (
          <div style={{ display: 'flex', gap: 14, marginBottom: 18 }}>
            {showCardFields && (
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Rarity</label>
                <input className="sv-input" style={{ marginTop: 6 }} list="sv-rarity-options" placeholder="e.g. Illustration Rare" value={form.rarity} onChange={(e) => setForm({ ...form, rarity: e.target.value })} />
                <datalist id="sv-rarity-options">
                  {RARITY_SUGGESTIONS.map((r) => <option key={r} value={r} />)}
                </datalist>
              </div>
            )}
            {showCardFields && (
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Number</label>
                <input className="sv-input" style={{ marginTop: 6 }} placeholder="e.g. 78/73" value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} />
              </div>
            )}
            {showSet && (
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>{kind === 'sealed' ? 'Set / product line' : 'Set name'}</label>
                <input className="sv-input" style={{ marginTop: 6 }} placeholder="e.g. Surging Sparks" value={form.set_name} onChange={(e) => setForm({ ...form, set_name: e.target.value })} />
              </div>
            )}
          </div>
        )}

        <label style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Volatility</label>
        <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: '4px 0 8px' }}>
          How often pricing needs revisiting — drives the "needs price check" queue on the products list.
        </p>
        <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
          {[
            { v: 'stable', label: 'Stable — set and forget' },
            { v: 'moderate', label: 'Moderate — check every few weeks' },
            { v: 'volatile', label: 'Volatile — check often' },
          ].map((opt) => (
            <button
              key={opt.v}
              type="button"
              onClick={() => setForm({ ...form, volatility: opt.v })}
              className={form.volatility === opt.v ? 'sv-btn' : 'sv-btn-outline'}
              style={{ flex: 1, fontSize: '0.8rem', padding: '10px 8px' }}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <details style={{ marginBottom: 18 }}>
          <summary style={{ fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', color: 'var(--text-muted)' }}>
            Shipping weight &amp; dimensions
          </summary>
          <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
            <input className="sv-input" placeholder="Weight (oz)" type="number" value={form.weight_oz} onChange={(e) => setForm({ ...form, weight_oz: e.target.value })} />
            <input className="sv-input" placeholder="L (in)" type="number" value={form.length_in} onChange={(e) => setForm({ ...form, length_in: e.target.value })} />
            <input className="sv-input" placeholder="W (in)" type="number" value={form.width_in} onChange={(e) => setForm({ ...form, width_in: e.target.value })} />
            <input className="sv-input" placeholder="H (in)" type="number" value={form.height_in} onChange={(e) => setForm({ ...form, height_in: e.target.value })} />
          </div>
        </details>

        <div className="sv-card" style={{ padding: 16, marginBottom: 20 }}>
          <label style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Photos</label>
          {isNew ? (
            <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', margin: '8px 0 0' }}>
              Save the product first, then add photos.
            </p>
          ) : (
            <>
              {media.length > 0 && (
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', margin: '10px 0' }}>
                  {media.map((m) => (
                    <div key={m.id} style={{ position: 'relative', width: 84, height: 84 }}>
                      <img
                        src={supabase.storage.from(MEDIA_BUCKET).getPublicUrl(m.storage_path).data.publicUrl}
                        alt=""
                        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border)' }}
                      />
                      <button
                        type="button"
                        onClick={() => removePhoto(m)}
                        style={{ position: 'absolute', top: -6, right: -6, background: 'var(--danger)', color: '#fff', border: 'none', borderRadius: 999, width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        aria-label="Remove photo"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <label
                onDragOver={(e) => { e.preventDefault(); if (!uploading) setDragActive(true); }}
                onDragLeave={() => setDragActive(false)}
                onDrop={handleDrop}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginTop: media.length > 0 ? 10 : 0,
                  padding: '20px 12px',
                  border: `2px dashed ${dragActive ? 'var(--accent, #b8860b)' : 'var(--border)'}`,
                  borderRadius: 10,
                  background: dragActive ? 'var(--hover, rgba(184,134,11,0.08))' : 'transparent',
                  cursor: uploading ? 'default' : 'pointer',
                  textAlign: 'center',
                }}
              >
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  disabled={uploading}
                  onChange={(e) => { const files = Array.from(e.target.files || []); if (files.length) uploadPhotos(files); e.target.value = ''; }}
                  style={{ display: 'none' }}
                />
                <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                  {uploading ? 'Uploading…' : 'Drag photos here, or click to select (multiple allowed)'}
                </span>
              </label>
              {uploading && <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 6 }}>Uploading…</p>}
              {mediaError && <p style={{ fontSize: '0.8rem', color: 'var(--danger)' }}>{mediaError}</p>}
              <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 8, marginBottom: 0 }}>
                Video upload needs ShinyVault's own Bunny Stream library — not set up yet, see PLAN.md.
              </p>
            </>
          )}
        </div>

        {!isNew && form.name && <RequestMatchPanel productId={id} productName={form.name} />}

        {error && <div style={{ color: 'var(--danger)', marginBottom: 14, fontSize: '0.85rem' }}>{error}</div>}

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button type="submit" disabled={saving} className="sv-btn">
            {saving ? 'Saving…' : isNew ? 'Create product' : 'Save changes'}
          </button>
          {!isNew && (
            <button type="button" disabled={saving} onClick={(e) => handleSave(e, true)} className="sv-btn-outline">
              Save &amp; mark price checked today
            </button>
          )}
        </div>
      </form>

      {!isNew && (
        <div style={{ marginTop: 32, paddingTop: 20, borderTop: '1px solid var(--border)' }}>
          <button
            type="button"
            disabled={saving || deleting}
            onClick={() => { setDeleteError(''); setConfirmingDelete(true); }}
            className="sv-btn-outline"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: 'var(--danger)', borderColor: 'var(--danger)' }}
          >
            <Trash2 size={16} /> Delete this product
          </button>
          <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: '8px 0 0' }}>
            Removes the item and its photos from the shop for good. Past orders that included it are kept.
          </p>
        </div>
      )}

      <ProductHistory productId={id} />

      {confirmingDelete && (
        <ConfirmDialog
          title="Delete this product?"
          message={
            deleteError
              ? `Couldn't delete: ${deleteError}`
              : `"${form.name}" and its photos will be permanently removed. Past orders that included it are kept. This can't be undone.`
          }
          confirmLabel="Delete product"
          busy={deleting}
          onConfirm={handleDelete}
          onCancel={() => { if (!deleting) { setConfirmingDelete(false); setDeleteError(''); } }}
        />
      )}
    </div>
  );
}
